import { useState } from 'react';
import { Lock, Bell, Palette, User, Sun, Moon, ArrowLeft, Sparkles, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useClientSettings, ThemeAccent, ThemeMode } from '../client-pwa/settings';

const accents: { key: ThemeAccent; label: string; gradient: string }[] = [
  { key: 'indigo', label: 'Indigo', gradient: 'from-indigo-500 to-violet-500' },
  { key: 'violet', label: 'Violet', gradient: 'from-violet-500 to-fuchsia-500' },
  { key: 'emerald', label: 'Emerald', gradient: 'from-emerald-400 to-cyan-500' },
  { key: 'sky', label: 'Sky', gradient: 'from-sky-400 to-blue-500' },
  { key: 'rose', label: 'Rose', gradient: 'from-rose-400 to-pink-500' },
];

export default function SettingsPageV2() {
  const { settings, update, themeMode, setThemeMode } = useClientSettings();
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaved, setPwdSaved] = useState(false);

  const handleSavePwd = (e: React.FormEvent) => {
    e.preventDefault();
    setPwdSaved(true);
    setPwd({ current: '', next: '', confirm: '' });
    setTimeout(() => setPwdSaved(false), 2000);
  };

  return (
    <div className="space-y-4 pt-6">
      <div className="flex items-center gap-3">
        <Link to="/client-pwa-v2" className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/70 backdrop-blur-md hover:bg-white/10">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-white/40">Preferences</p>
          <h1 className="text-2xl font-black tracking-tighter">Settings</h1>
        </div>
      </div>

      <Section icon={User} title="Account">
        <Row label="Name" value="James Halloran" />
        <Row label="Email" value="james@jamestiling.com.au" />
        <Row label="Company" value="James Tiling Pty Ltd" />
      </Section>

      <Section icon={Palette} title="Appearance">
        <div className="space-y-5 px-4 py-4">
          <div>
            <p className="mb-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">Accent</p>
            <div className="flex gap-2.5">
              {accents.map(a => {
                const isActive = settings.accent === a.key;
                return (
                  <button
                    key={a.key}
                    onClick={() => update('accent', a.key)}
                    className={`relative h-12 w-12 overflow-hidden rounded-2xl bg-gradient-to-br ${a.gradient} ring-offset-2 ring-offset-[#2a2a3a] transition-all ${
                      isActive ? 'scale-110 ring-2 ring-white/60 shadow-lg' : 'opacity-70 hover:opacity-100'
                    }`}
                    aria-label={a.label}
                  >
                    {isActive && <div className="flex h-full w-full items-center justify-center"><Check className="h-4 w-4 text-white" /></div>}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="mb-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">Theme</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'light' as ThemeMode, label: 'Light', icon: Sun },
                { key: 'dark' as ThemeMode, label: 'Dark', icon: Moon },
              ].map(m => {
                const Icon = m.icon;
                const isActive = themeMode === m.key;
                return (
                  <button
                    key={m.key}
                    onClick={() => setThemeMode(m.key)}
                    className={`flex flex-col items-center gap-1 rounded-2xl border py-3.5 text-xs font-semibold transition-all ${
                      isActive
                        ? 'border-violet-400 bg-gradient-to-br from-violet-500/30 to-fuchsia-500/20 text-white'
                        : 'border-white/10 bg-white/5 text-white/50 hover:bg-white/10'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {m.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-[10px] text-white/40">Note: v2 is designed for dark mode</p>
          </div>
        </div>
      </Section>

      <Section icon={Bell} title="Notifications">
        <ToggleRow label="Email notifications" value={settings.emailNotifications} onChange={v => update('emailNotifications', v)} />
        <Divider />
        <ToggleRow label="Push notifications" value={settings.pushNotifications} onChange={v => update('pushNotifications', v)} />
      </Section>

      <Section icon={Lock} title="Change password">
        <form onSubmit={handleSavePwd} className="space-y-3 px-4 py-4">
          {[
            { v: pwd.current, p: 'Current password', set: (x: string) => setPwd({ ...pwd, current: x }) },
            { v: pwd.next, p: 'New password', set: (x: string) => setPwd({ ...pwd, next: x }) },
            { v: pwd.confirm, p: 'Confirm new password', set: (x: string) => setPwd({ ...pwd, confirm: x }) },
          ].map((f, i) => (
            <input
              key={i}
              type="password"
              value={f.v}
              onChange={e => f.set(e.target.value)}
              placeholder={f.p}
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-violet-400 focus:outline-none"
            />
          ))}
          <button
            type="submit"
            disabled={!pwd.current || !pwd.next || pwd.next !== pwd.confirm}
            className={`w-full rounded-2xl py-3.5 text-sm font-bold transition-all ${
              pwdSaved
                ? 'bg-emerald-400 text-[#2a2a3a]'
                : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-violet-500/30 hover:shadow-xl disabled:opacity-30 disabled:shadow-none'
            }`}
          >
            {pwdSaved ? '✓ Saved' : 'Save changes'}
          </button>
        </form>
      </Section>

      {/* About */}
      <div className="pt-2 pb-4 text-center">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/40">
          <span>Powered by</span>
          <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-amber-300 bg-clip-text font-bold text-transparent">RoniDoc</span>
        </div>
        <p className="mt-1 text-[9px] text-white/30">v2.0.0 · The Studio Edition</p>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="mb-2 flex items-center gap-2 px-1 text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">
        <Icon className="h-3 w-3" />
        {title}
      </h2>
      <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 px-4 py-3.5 last:border-0">
      <span className="text-sm text-white/60">{label}</span>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  );
}

function Divider() { return <div className="h-px bg-white/5" />; }

function ToggleRow({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between px-4 py-3.5">
      <span className="text-sm">{label}</span>
      <button
        onClick={() => onChange(!value)}
        className={`relative h-6 w-11 rounded-full transition-colors ${value ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500' : 'bg-white/10'}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}
